import { t } from "../i18n"
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["item", "progressBar", "progressText", "saveButton"]
  static values = { 
    taskId: Number,
    companyId: Number,
    items: Array
  }

  connect() {
    this.localItems = this.itemsValue || []
    this.hasChanges = false
    this.updateProgress()
    this.restoreUIState()
  }

  restoreUIState() {
    // Restore UI state for all items based on saved data
    this.localItems.forEach((item, index) => {
      const itemEl = this.element.querySelector(`[data-checklist-item-id="${index}"]`)
      if (!itemEl) return

      // Restore pass/fail button states
      if (item.passed !== undefined && item.passed !== null) {
        const buttons = itemEl.querySelectorAll('button[data-action*="submitPassFail"]')
        buttons.forEach(btn => {
          const btnValue = btn.dataset.value === "true"
          const isSelected = (item.passed === true && btnValue) || (item.passed === false && !btnValue)
          
          // Reset classes
          btn.className = "flex-1 px-4 py-3 rounded-lg border-2 transition-all"
          
          if (isSelected) {
            if (btnValue) {
              btn.classList.add('bg-green-600', 'border-green-500', 'text-white')
            } else {
              btn.classList.add('bg-red-600', 'border-red-500', 'text-white')
            }
          } else {
            btn.classList.add('bg-gray-800', 'border-gray-600', 'text-gray-300')
            if (btnValue) {
              btn.classList.add('hover:border-green-500')
            } else {
              btn.classList.add('hover:border-red-500')
            }
          }
        })
      }

      // Restore number input values
      if (item.number_value !== undefined && item.number_value !== null) {
        const numberInput = itemEl.querySelector(`input[type="number"][data-item-id="${index}"]`)
        if (numberInput) {
          numberInput.value = item.number_value
        }
      }

      // Restore text input values
      if (item.text_value !== undefined && item.text_value !== null) {
        const textInput = itemEl.querySelector(`textarea[data-item-id="${index}"]`)
        if (textInput) {
          textInput.value = item.text_value
        }
      }

      // Restore completion indicator
      const isCompleted = item.completed ||
             (item.passed !== null && item.passed !== undefined) ||
             (item.text_value && item.text_value.length > 0) ||
             (item.number_value !== null && item.number_value !== undefined) ||
             (item.boolean_value !== null && item.boolean_value !== undefined)
      const indicator = itemEl.querySelector('.material-symbols-outlined:last-child')
      if (indicator) {
        if (isCompleted) {
          indicator.textContent = 'check_circle' // Material Symbols ligature
          indicator.classList.remove('text-gray-600')
          indicator.classList.add('text-green-400')
        } else {
          indicator.textContent = 'radio_button_unchecked' // Material Symbols ligature
          indicator.classList.remove('text-green-400')
          indicator.classList.add('text-gray-600')
        }
      }
    })
  }

  submitPassFail(event) {
    const button = event.currentTarget
    const index = parseInt(button.dataset.itemId)
    const value = button.dataset.value === "true"
    
    this.updateLocalItem(index, { passed: value })
    
    // Update UI for pass/fail buttons
    const container = button.closest('.flex')
    const buttons = container.querySelectorAll('button')
    buttons.forEach(btn => {
      const btnValue = btn.dataset.value === "true"
      const isSelected = btnValue === value
      
      // Reset classes
      btn.className = "flex-1 px-4 py-3 rounded-lg border-2 transition-all"
      
      if (isSelected) {
        if (value) {
          btn.classList.add('bg-green-600', 'border-green-500', 'text-white')
        } else {
          btn.classList.add('bg-red-600', 'border-red-500', 'text-white')
        }
      } else {
        btn.classList.add('bg-gray-800', 'border-gray-600', 'text-gray-300')
        if (btnValue) {
          btn.classList.add('hover:border-green-500')
        } else {
          btn.classList.add('hover:border-red-500')
        }
      }
    })
  }

  submitCheckbox(event) {
    const checkbox = event.target
    const index = parseInt(checkbox.dataset.itemId)
    const value = checkbox.checked
    
    this.updateLocalItem(index, { completed: value, boolean_value: value })
  }

  submitText(event) {
    const textarea = event.target
    const index = parseInt(textarea.dataset.itemId)
    const value = textarea.value
    
    this.updateLocalItem(index, { text_value: value })
  }

  submitDropdown(event) {
    const select = event.target
    const index = parseInt(select.dataset.itemId)
    const value = select.value

    if (value !== "") {
      this.updateLocalItem(index, { text_value: value, completed: true })
    }
  }

  submitNumber(event) {
    const input = event.target
    const index = parseInt(input.dataset.itemId)
    const value = parseFloat(input.value)
    
    if (!isNaN(value)) {
      this.updateLocalItem(index, { number_value: value })
    }
  }

  updateLocalItem(index, changes) {
    if (this.localItems[index]) {
      this.localItems[index] = { ...this.localItems[index], ...changes }
      
      // Update completion status — treat explicit false for boolean/pass_fail as a completed answer
      const item = this.localItems[index]
      const isCompleted = item.completed ||
             (item.passed !== null && item.passed !== undefined) ||
             (item.text_value && item.text_value.length > 0) ||
             (item.number_value !== null && item.number_value !== undefined) ||
             (item.boolean_value !== null && item.boolean_value !== undefined)
      this.localItems[index].completed = !!isCompleted // Ensure boolean
      
      this.hasChanges = true
      this.updateProgress()
      this.showSaveButton()
      
      // Update item completion indicator (circle icon)
      const itemEl = this.element.querySelector(`[data-checklist-item-id="${index}"]`)
      if (itemEl) {
        const indicator = itemEl.querySelector('.material-symbols-outlined:last-child')
        if (indicator) {
          if (isCompleted) {
            indicator.textContent = 'check_circle' // Material Symbols ligature
            indicator.classList.remove('text-gray-600')
            indicator.classList.add('text-green-400')
          } else {
            indicator.textContent = 'radio_button_unchecked' // Material Symbols ligature
            indicator.classList.remove('text-green-400')
            indicator.classList.add('text-gray-600')
          }
        }
      }
    }
  }

  showSaveButton() {
    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.classList.remove('hidden')
      this.saveButtonTarget.classList.add('animate-pulse')
    }
  }

  async save() {
    if (!this.hasChanges) return

    const button = this.saveButtonTarget
    const originalText = button.innerHTML
    button.disabled = true
    button.innerHTML = `<span class="material-symbols-outlined animate-spin">refresh</span> ${t("javascript.controllers.task_checklist_controller.saving")}`
    button.classList.remove('animate-pulse')

    try {
      const response = await fetch(`/api/tasks/${this.taskIdValue}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken,
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          task: {
            checklist_items: this.localItems
          }
        })
      })

      if (response.ok) {
        // Reload page to reflect state (colors, progress, start button)
        window.location.reload()
      } else {
        const data = await response.json()
        alert(t("javascript.controllers.task_checklist_controller.failed_to_save_checklist"))
        button.disabled = false
        button.innerHTML = originalText
      }
    } catch (error) {
      console.error('Error saving checklist:', error)
      alert(t('javascript.controllers.task_checklist_controller.an_error_occurred_please_try_again'))
      button.disabled = false
      button.innerHTML = originalText
    }
  }

  updateProgress() {
    const total = this.localItems.length
    if (total === 0) return

    const completed = this.localItems.filter(item => {
      // Use the logic from our updateLocalItem or simplified check
      // For tasks, 'completed' field in JSON is the master flag
      return item.completed === true || item.completed === 'true'
    }).length
    
    const percentage = Math.round((completed / total) * 100)

    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${percentage}%`
    }

    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = `${percentage}%`
    }
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}